import { createApiClient } from "#/shared/api/client";
import { ContentType, HeaderName, HttpError, HttpStatus, REQUEST_TIMEOUT_MS } from "#/shared/api/http";

// Successful statuses that never carry a body.
const EMPTY_SUCCESS_STATUSES = Object.freeze([HttpStatus.NO_CONTENT, 205]);

function isJson(response) {
  const contentType = response.headers.get(HeaderName.CONTENT_TYPE) ?? "";

  return contentType.includes(ContentType.JSON) || contentType.includes("+json");
}

// Rejects with the signal's reason once it aborts.
function whenAborted(signal) {
  return new Promise(function waitForAbort(_resolve, reject) {
    function rejectWithReason() {
      reject(signal.reason);
    }

    signal.addEventListener("abort", rejectWithReason, { once: true });
  });
}

// A body that is not JSON, such as a sign-in page reached through a redirect, rejects with the
// response's own status.
async function readJson(response, signal) {
  const text = await Promise.race([response.text(), whenAborted(signal)]);

  if (!text) {
    return null;
  }

  if (!isJson(response)) {
    const contentType = response.headers.get(HeaderName.CONTENT_TYPE) || "no content type";

    throw new HttpError(`Expected a JSON response, got ${contentType}`, { status: response.status, data: text });
  }

  return JSON.parse(text);
}

/**
 * Builds the client that sends an `sp-action-form`'s slotted form.
 *
 * @returns {{ submit: (form: HTMLFormElement) => Promise<*> }} `submit` sends the form to its
 *   action with fetch, asking for JSON, and resolves the parsed body, or `null` for an empty one.
 *   A GET form sends its fields as the query. Any other form posts them as `FormData`, so a Rails
 *   `_method` field is sent with them. Rejects with `HttpError` on a non-2xx response, a
 *   successful response that is not JSON, a timeout or a network failure.
 */
export const createActionFormApi = () => {
  const client = createApiClient({ parse: false });

  function send(form, signal) {
    const fields = new FormData(form);

    if (form.method === "get") {
      return client.stream("get", form.action, { params: new URLSearchParams(fields), signal });
    }

    return client.stream("post", form.action, { data: fields, signal });
  }

  // One timeout covers the headers and the body, so a stalled body cannot keep the form pending.
  async function submit(form) {
    const controller = new AbortController();
    const timer = setTimeout(abortRequest, REQUEST_TIMEOUT_MS);

    function abortRequest() {
      controller.abort(new HttpError(`Request timed out after ${REQUEST_TIMEOUT_MS}ms`));
    }

    try {
      return await readJson(await send(form, controller.signal), controller.signal);
    } catch (error) {
      // `stream` rejects a 204 or 205 because it has no body to read; for a form action that is success.
      if (error instanceof HttpError && EMPTY_SUCCESS_STATUSES.includes(error.status)) {
        return null;
      }

      throw error;
    } finally {
      clearTimeout(timer);
    }
  }

  return { submit };
};
